/**
 * @fileoverview Conflict-check entity picker helpers (MOR-1419)
 *
 * Purpose: the shape the composer keeps for the further entities a check
 * covers, and its translation into the `additionalEntities` argument that
 * `conflicts.getSuggestedParties` and `conflicts.create` take. Kept out of
 * the picker component so the panel and its tests can build the argument
 * without mounting anything.
 *
 * Used by:
 *  - conflict-entity-picker.tsx
 *  - pipeline-job-panel.tsx
 */

import { format } from "date-fns";
import type { Id } from "@cvx/_generated/dataModel";
import type { ConflictCheckEntityInput } from "@cvx/preAppointment/conflictChecks/entities.schema";

/**
 * One further entity in the composer: a sibling pipeline job (whose searches
 * feed the brief) or, with `jobId` null, a name typed for an entity that has
 * no job on the platform yet.
 */
export interface ConflictEntityDraft {
  jobId: Id<"jobs"> | null;
  name: string;
  /**
   * What tells this job apart from another with the same entity name (a
   * job code, or a short reference and created date) — see
   * {@link describePipelineJob}. Absent for an entity typed by hand.
   */
  context?: string;
}

/** The pipeline-job fields the picker reads to describe a job. */
export interface PipelineJobIdentity {
  _id: Id<"jobs">;
  _creationTime: number;
  entityName: string;
  jobName?: string;
  jobCode?: string;
}

/**
 * The context that tells one pipeline job from another with the same entity
 * name — a firm can hold several pitches for one company, and each carries
 * its own searches and audit record. The job code leads when the firm
 * assigns them; otherwise a short reference (the tail of the job id, which
 * never changes) with the created date. A job name that differs from the
 * entity name is appended either way. Returned alongside the search terms
 * the picker matches on, so a practitioner can find a job by its code.
 */
export function describePipelineJob(job: PipelineJobIdentity): {
  context: string;
  keywords: string[];
} {
  const jobName = job.jobName?.trim() ?? "";
  const distinctJobName =
    jobName && jobName.toLowerCase() !== job.entityName.trim().toLowerCase()
      ? jobName
      : null;
  const code = job.jobCode?.trim() || null;
  const created = `created ${format(job._creationTime, "d MMM yyyy")}`;
  const reference = `#${String(job._id).slice(-4)}`;
  const parts = code
    ? [code, distinctJobName]
    : [reference, created, distinctJobName];
  const context = parts
    .filter((part): part is string => part !== null)
    .join(" · ");
  return {
    context,
    keywords: [code, reference, distinctJobName, created].filter(
      (part): part is string => part !== null,
    ),
  };
}

/** The composer's drafts as the backend's `additionalEntities` argument. */
export function toEntityInputs(
  entities: ConflictEntityDraft[],
): ConflictCheckEntityInput[] {
  return entities.map((entity) =>
    entity.jobId ? { jobId: entity.jobId } : { name: entity.name },
  );
}

/**
 * Add a draft unless the same job, or the same name in any case, is already
 * listed — the anchoring job's name counts as listed too.
 */
export function addEntityDraft(
  entities: ConflictEntityDraft[],
  draft: ConflictEntityDraft,
  anchorName: string,
): ConflictEntityDraft[] {
  const name = draft.name.trim();
  if (!name) return entities;
  const key = name.toLowerCase();
  const taken =
    anchorName.trim().toLowerCase() === key ||
    entities.some(
      (entity) =>
        (draft.jobId !== null && entity.jobId === draft.jobId) ||
        entity.name.trim().toLowerCase() === key,
    );
  return taken
    ? entities
    : [
        ...entities,
        draft.context !== undefined
          ? { jobId: draft.jobId, name, context: draft.context }
          : { jobId: draft.jobId, name },
      ];
}
